import { formatDateToString } from 'shared/dateFns'
import { type FormatToMoneyProps, formatToPercentWithOptions } from 'shared/numberFormat'
import type { ElementDescription, ElementDescriptionDimension, ElementDescriptionMetric } from '../internalTypes'
import type {
  ChartCardDefaultsFormat,
  FormatToNumberProps,
  ChartCardGridSize,
  ChartCardTFunction,
  ChartDataValue,
  SizeProps,
  ViewType
} from '../types'
import {
  defaultTranslations,
  NULL_DIMENSION_VALUE_DISPLAY,
  NULL_DIMENSION_VALUE_OPTION,
  OthersIDSuffix
} from '../constants'
import type { Entries } from 'type-fest'
import { defaultTo } from 'ramda'
import type { EvalFunction } from 'mathjs'

export const objectKeys = <T extends Record<string, unknown>>(obj: T): Array<keyof T> => Object.keys(obj)

export const objectEntries = <T extends object>(obj: T): Entries<T> => Object.entries(obj) as Entries<T>
export const objectValues = <T extends object>(obj: T): Array<T[keyof T]> => Object.values(obj) as Array<T[keyof T]>

export const objectFromEntries = <T extends ReadonlyArray<readonly [PropertyKey, unknown]>>(
  entries: T
): { [K in T[number] as K[0]]: K[1] } => {
  return Object.fromEntries(entries) as { [K in T[number] as K[0]]: K[1] }
}

export const defaultToZero = defaultTo(0)

const pluralsToSingle = [
  ['ses', 's'],
  ['xes', 'x'],
  ['shes', 'sh'],
  ['ches', 'ch'],
  ['ies', 'y'],
  ['ays', 'ay'],
  ['eys', 'ey'],
  ['iys', 'iy'],
  ['oys', 'oy'],
  ['uys', 'uy'],
  ['ves', 'f'],
  ['oes', 'o'],
  ['s', '']
]

export const pluralToSingle = (string: string, number: number): string => {
  if (number === 1) {
    const plural = pluralsToSingle.find(([plural]) => string.endsWith(plural))
    if (plural != null) {
      return string.slice(0, -plural[0].length) + plural[1]
    }
    return string
  }
  return string
}

export const singleToPlural = (string: string, number: number): string => {
  if (number === 1) {
    return pluralToSingle(string, number)
  }

  string = pluralToSingle(string, 1)

  const single = pluralsToSingle.find(([, single]) => string.endsWith(single))
  if (single != null && single[1] !== '') {
    return string.slice(0, -single[1].length) + single[0]
  }
  if (!string.endsWith('s')) {
    return string + 's'
  }

  return string
}

export const assertNever = (value: never): never => {
  errorLocal('assertNever', { value })
  throw new Error(`Unexpected value: ${String(value)}`)
}

export function assertNotNull<T>(value: T | null | undefined): asserts value is T {
  if (value == null) {
    throw new Error('Value is null or undefined')
  }
}

export const transposedMetricColumn = <DataKey extends string>(metric: DataKey, value: string): DataKey =>
  `${metric}:${value}` as DataKey

export const getValueFromTransposedMetricColumn = <DataKey extends string>(metric: DataKey): string =>
  metric.replace(/^[^:]+:(.*)$/, '$1')

export const getMetricFromTransposedMetricColumn = <DataKey extends string>(metric: DataKey): DataKey =>
  metric.replace(/^([^:]+):.*$/, '$1') as DataKey

export const generateIdForSelectionOthers = <DataKey extends string>(id: DataKey): DataKey =>
  `${id}${OthersIDSuffix}` as DataKey

export const isSelectionOthersId = <DataKey extends string>(id: DataKey): boolean => id.endsWith(OthersIDSuffix)

export const getIdFromSelectionOthersId = <DataKey extends string>(id: DataKey): DataKey =>
  id.slice(0, -OthersIDSuffix.length) as DataKey

export const formatToMoney = ({
  value,
  currencyCode = 'EUR',
  compact = true,
  showCurrency = true,
  minimumFractionDigits = 0,
  maximumFractionDigits = 1
}: FormatToMoneyProps): string => {
  const compactNumberFormat: Intl.NumberFormatOptions = {
    notation: 'compact',
    compactDisplay: 'short'
  }

  const defaultNumberFormat: Intl.NumberFormatOptions = {
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits,
    maximumFractionDigits,
    ...(showCurrency && { style: 'currency' })
  }

  const formatOptions = compact ? { ...defaultNumberFormat, ...compactNumberFormat } : defaultNumberFormat

  return Intl.NumberFormat('en-US', formatOptions).format(value)
}

export const formatToNumber = ({
  value,
  compact = true,
  minimumFractionDigits = 0,
  maximumFractionDigits = 1,
  ...options
}: FormatToNumberProps & { value: number }): string => {
  const compactNumberFormat: Intl.NumberFormatOptions = {
    notation: 'compact',
    compactDisplay: 'short'
  }

  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits,
    maximumFractionDigits,
    ...(compact && compactNumberFormat),
    ...options
  }

  const formattedValue = Intl.NumberFormat('en-US', formatOptions).format(value)
  if (formattedValue === '-0') {
    return '0'
  }
  return formattedValue
}

export const isLocalHostname = (hostname: string): boolean => hostname === 'localhost' || hostname === '127.0.0.1'

export const localBrowser = {
  isActive: (): boolean => globalThis.window !== undefined && isLocalHostname(globalThis.location.hostname)
}

export const warningLocal = (...args: any[]): void => {
  if (localBrowser.isActive()) {
    // eslint-disable-next-line no-console
    console.warn('ChartCard', ...args)
  }
}

export const errorLocal = (...args: any[]): void => {
  if (localBrowser.isActive()) {
    // eslint-disable-next-line no-console
    console.error('ChartCard', ...args)
  }
}

export const isBelowDomain = (value: number, domain: readonly [number, number] | undefined): boolean => {
  if (domain == null) {
    return true
  }
  return value < domain[0]
}

export const clipToDomain = (value: number, domain: readonly [number, number] | undefined): number => {
  if (domain == null) {
    return value
  }
  return Math.max(Math.min(value, domain[1]), domain[0])
}

type StrictObjectEntry<BaseType> = [keyof BaseType, Exclude<BaseType[keyof BaseType], undefined>]
type StrictObjectValue<BaseType> = Exclude<BaseType[keyof BaseType], undefined>

export const strictObjectEntries = <T extends object>(obj: T): Array<StrictObjectEntry<T>> =>
  Object.entries(obj) as Array<StrictObjectEntry<T>>

export const strictObjectValues = <T extends object>(obj: T): Array<StrictObjectValue<T>> =>
  Object.values(obj) as Array<StrictObjectValue<T>>

/**
 * A dimension value is considered missing when the source data has no value for it.
 *
 * Empty strings count as missing because the chart data pipeline normalises them to null
 * (see `getStringDimensionValue`), and pre-processed data can still carry them through.
 *
 * {@link NULL_DIMENSION_VALUE_OPTION} counts as missing too: it is the value charts, options,
 * and selections carry for a missing dimension, so every label path renders it as
 * {@link NULL_DIMENSION_VALUE_DISPLAY} with a "No data" tooltip.
 */
export const isNullDimensionValue = (value: ChartDataValue | null | undefined): boolean =>
  value == null || value === '' || value === NULL_DIMENSION_VALUE_OPTION

/**
 * Canonical selection/filter value for a dimension cell.
 * Missing values share {@link NULL_DIMENSION_VALUE_OPTION} so charts can keep a null slice selectable.
 */
export const toNullDimensionOptionValue = (value: ChartDataValue | null | undefined): string =>
  isNullDimensionValue(value) ? NULL_DIMENSION_VALUE_OPTION : String(value)

export const formatMetricValue = <DataKey extends string>(
  id: DataKey,
  elementDescription: ElementDescriptionMetric,
  value: ChartDataValue | undefined,
  defaults: ChartCardDefaultsFormat
): string => {
  const { type, format } = elementDescription

  if (typeof format === 'function') {
    return format(id, Number(value))
  } else {
    switch (type) {
      case 'currency':
        return formatToMoney({
          currencyCode: 'EUR',
          ...defaults.money,
          ...format,
          value: Number(value ?? 0)
        })
      case 'number':
        return formatToNumber({
          value: Number(value ?? 0),
          ...defaults.number,
          ...format
        })
      case 'percentage':
        return formatToPercentWithOptions(Number(value ?? 0), {
          ...defaults.percentage,
          ...format
        })
      default:
        assertNever(type)
        return 'N/A'
    }
  }
}

export const formatDateValue = <DataKey extends string>(
  id: DataKey,
  elementDescription: ElementDescriptionDimension,
  value: ChartDataValue | undefined
): string => {
  const { format } = elementDescription

  if (typeof format === 'function') return format(id, value) ?? ''
  else if (typeof format === 'string') return formatDateToString({ date: value as string, targetFormat: format })
  else if (typeof format === 'object') return formatDateToString({ date: value as string, ...format })
  else return formatDateToString({ date: value as string, targetFormat: 'yyyy-MM-dd' })
}

// Cast: i18next TFunction is branded/overloaded; ChartCard only calls t(key) / t(key, props).
export const defaultTFunction = ((key: keyof typeof defaultTranslations, props?: Record<string, string>) => {
  if (props != null) {
    return defaultTranslations[key].replaceAll(/{{(.*?)}}/g, (match, p1: string) => props[p1] ?? match)
  }
  return defaultTranslations[key]
}) as ChartCardTFunction

export const getWidthAndHeightFromSizeProps = (
  view: ViewType,
  size: SizeProps,
  sizeToMinWidth: Record<ChartCardGridSize, number>,
  sizeToMinHeight: Record<ChartCardGridSize, number>
): { width: number | undefined; height: number | undefined } => {
  let width = size.width ?? size.minWidth
  if (width == null && size.size != null) {
    width = sizeToMinWidth[size.size]
  }

  let height = size.height ?? size.minHeight
  if (height == null && size.size != null) {
    height = sizeToMinHeight[size.size]
  }

  if (view === 'polar') {
    width ??= height
    height ??= width
  }

  return { width, height }
}

export const evaluateFormula = (formula: EvalFunction, scope: Record<string, number>): number => {
  const rawResult = formula.evaluate(scope)
  return typeof rawResult === 'number' && Number.isFinite(rawResult) ? rawResult : 0
}

export const setDifference = <T>(set1: Set<T>, set2: Set<T>): Set<T> => {
  if (set1.difference != null) {
    return set1.difference(set2)
  }
  return new Set([...set1].filter((item) => !set2.has(item)))
}

export const setDifferenceAsArray = <T>(set1: Set<T>, set2: Set<T>): T[] => {
  if (set1.difference != null) {
    return Array.from(set1.difference(set2))
  }
  return [...set1].filter((item) => !set2.has(item))
}

export const arrayToSorted = <T>(array: T[], compareFn: (a: T, b: T) => number): T[] => {
  if (array.toSorted != null) {
    return array.toSorted(compareFn)
  }
  return [...array].sort(compareFn)
}

export const getLabelOrValueAsLabel = (
  label: string | undefined,
  value: ChartDataValue | null | undefined,
  showValueAsIs = true
): string => {
  if (isNullDimensionValue(value)) {
    return NULL_DIMENSION_VALUE_DISPLAY
  }
  if (label != null) {
    return label
  }
  if (showValueAsIs) {
    return String(value)
  }
  return `[${value}]`
}

/**
 * Label for a string or variant dimension value.
 * A mapped description wins. Otherwise a format function (used for numeric dimensions) renders the value.
 */
export const getDimensionLabel = (
  element: ElementDescription | undefined,
  value: ChartDataValue | null | undefined,
  dataKey?: string
): string => {
  const mappedLabel =
    value == null || element == null || !('descriptions' in element)
      ? undefined
      : element.descriptions?.[String(value)]?.label
  if (mappedLabel != null) {
    return getLabelOrValueAsLabel(mappedLabel, value)
  }

  if (
    (element?.type === 'string' || element?.type === 'variant') &&
    typeof element.format === 'function' &&
    dataKey != null &&
    !isNullDimensionValue(value)
  ) {
    const formatted = element.format(dataKey, value)
    if (formatted != null && formatted !== '') {
      return formatted
    }
  }

  return getLabelOrValueAsLabel(undefined, value)
}
